/**
 * Agent detail route (§4.4).
 *
 * URL: /agents/:name/:tab? — router uses /agents/:name/* so the nested
 * tab slug comes in as `tabSlug` via useLocation. We don't use a second
 * router — tab state lives in the URL and Overview is the default.
 *
 * Subagent C wires the Layout outside; this component renders the
 * header + tabs + tab contents. Header action dropdown kicks off
 * start/stop/restart mutations, each of which enters the deploy drawer.
 */
import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ChevronDown,
  Loader2,
  Play,
  RotateCw,
  Square,
  Trash2,
} from 'lucide-react';
import { useAgent } from '@/hooks/useAgent';
import { useAgentAction } from '@/hooks/useAgents';
// Deploy drawer removed — v2.0 operations are synchronous.
import { EmptyState } from '@/components/EmptyState';
import { GlobalOverlays } from '@/components/GlobalOverlays';
import { StatusBadge } from '@/components/StatusBadge';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ApiError } from '@/api/client';
import Overview from '@/routes/agent-tabs/Overview';
import Config from '@/routes/agent-tabs/Config';
import Channels from '@/routes/agent-tabs/Channels';
import Memory from '@/routes/agent-tabs/Memory';
import Logs from '@/routes/agent-tabs/Logs';
import Conversations from '@/routes/agent-tabs/Conversations';
import MCPs from '@/routes/agent-tabs/MCPs';

const TABS = [
  { slug: 'overview', label: 'Overview' },
  { slug: 'config', label: 'Config' },
  { slug: 'channels', label: 'Channels' },
  { slug: 'mcps', label: 'MCPs' },
  { slug: 'memory', label: 'Memory' },
  { slug: 'logs', label: 'Logs' },
  { slug: 'conversations', label: 'Conversations' },
] as const;

type TabSlug = (typeof TABS)[number]['slug'];

function parseTabSlug(pathname: string, name: string): TabSlug {
  const prefix = `/agents/${encodeURIComponent(name)}`;
  const rest = pathname.startsWith(prefix) ? pathname.slice(prefix.length) : pathname;
  const cleaned = rest.replace(/^\//, '').split('/')[0] ?? '';
  const match = TABS.find((t) => t.slug === cleaned);
  return (match?.slug ?? 'overview') as TabSlug;
}

export default function AgentDetail() {
  const { name } = useParams<{ name: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const agentQuery = useAgent(name);
  const action = useAgentAction();

  const currentTab = useMemo(
    () => parseTabSlug(location.pathname, name ?? ''),
    [location.pathname, name]
  );

  async function handleAction(kind: 'start' | 'stop' | 'restart') {
    if (!name) return;
    try {
      await action.mutateAsync({ name, action: kind });
    } catch (err) {
      console.error(err instanceof ApiError ? err.message : err);
    }
  }

  function goTab(slug: TabSlug) {
    if (!name) return;
    const path = slug === 'overview'
      ? `/agents/${encodeURIComponent(name)}`
      : `/agents/${encodeURIComponent(name)}/${slug}`;
    navigate(path);
  }

  if (agentQuery.isLoading) {
    return (
      <div className="space-y-6 p-4 md:p-6">
        <LoadingSkeleton className="h-8 w-64" />
        <LoadingSkeleton className="h-4 w-96" />
        <div className="space-y-2">
          <LoadingSkeleton className="h-10 w-full" />
          <LoadingSkeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  if (agentQuery.isError || !agentQuery.data) {
    return (
      <div className="p-4 md:p-6">
        <EmptyState
          title="Agent not found"
          description={
            agentQuery.error instanceof ApiError
              ? agentQuery.error.detail ?? agentQuery.error.title
              : 'The agent you asked for does not exist (or the backend is down).'
          }
          action={
            <Button variant="outline" onClick={() => navigate('/agents')}>
              Back to Agents
            </Button>
          }
        />
      </div>
    );
  }

  const agent = agentQuery.data;

  return (
    <div className="flex flex-col p-4 md:p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-semibold tracking-tight truncate">
              {agent.name}
            </h1>
            <StatusBadge status={agent.enabled ? 'active' : 'inactive'} />
          </div>
          {agent.description && (
            <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
              {agent.description}
            </p>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" disabled={action.isPending}>
              {action.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              Actions
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleAction('start')}>
              <Play className="h-4 w-4" />
              Start
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleAction('restart')}>
              <RotateCw className="h-4 w-4" />
              Restart
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleAction('stop')}>
              <Square className="h-4 w-4" />
              Stop
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => goTab('overview')}
            >
              <Trash2 className="h-4 w-4" />
              Delete (in Overview → Danger zone)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Tabs
        value={currentTab}
        onValueChange={(v) => goTab(v as TabSlug)}
        className="flex-1 flex flex-col min-h-0"
      >
        <TabsList>
          {TABS.map((t) => (
            <TabsTrigger key={t.slug} value={t.slug}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <div className="mt-4 flex-1 min-h-0">
          {currentTab === 'overview' && <Overview agent={agent} />}
          {currentTab === 'config' && <Config agent={agent} />}
          {currentTab === 'channels' && <Channels agent={agent} />}
          {currentTab === 'mcps' && <MCPs agent={agent} />}
          {currentTab === 'memory' && <Memory agent={agent} />}
          {currentTab === 'logs' && <Logs agent={agent} />}
          {currentTab === 'conversations' && <Conversations agent={agent} />}
        </div>
      </Tabs>
      <GlobalOverlays />
    </div>
  );
}
